import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle, XCircle, ArrowRight, Spinner } from "@phosphor-icons/react";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const reference = params.get("reference") || params.get("trxref");
  const [state, setState] = useState({ loading: true, ok: false, message: "", data: null });

  useEffect(() => {
    if (!reference) {
      setState({ loading: false, ok: false, message: "Missing payment reference." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/subscription/verify/${encodeURIComponent(reference)}`);
        if (data.ok) {
          try { await refresh(); } catch {}
          setState({ loading: false, ok: true, message: "Payment confirmed", data });
        } else {
          setState({ loading: false, ok: false, message: data.message || "Payment could not be verified", data });
        }
      } catch (err) {
        setState({ loading: false, ok: false, message: extractApiError(err, "Verification failed") });
      }
    })();
  }, [reference, refresh]);

  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center px-4 py-16" data-testid="payment-callback-page">
      <div className="max-w-lg w-full bg-card border border-border rounded-2xl p-8 sm:p-10 card-elev text-center">
        {state.loading ? (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-teal-soft text-teal grid place-items-center mb-5">
              <Spinner size={28} weight="duotone" className="animate-spin" />
            </div>
            <div className="text-overline text-primary mb-2">Verifying payment</div>
            <h1 className="font-display text-2xl tracking-tight">Confirming your transaction…</h1>
            <p className="mt-3 text-sm text-muted-foreground">This usually takes only a few seconds.</p>
          </>
        ) : state.ok ? (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-teal-soft text-teal grid place-items-center mb-5">
              <CheckCircle size={30} weight="fill" />
            </div>
            <div className="text-overline text-primary mb-2">Payment confirmed</div>
            <h1 className="font-display text-3xl tracking-tight">Your workspace is upgraded.</h1>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Thank you — your subscription is now active. A receipt has been emailed to you.
            </p>
            {state.data?.reference && (
              <div className="mt-6 inline-flex items-center gap-2 bg-secondary rounded-full px-4 py-1.5 text-xs font-mono text-muted-foreground">
                Reference · {state.data.reference}
              </div>
            )}
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3 text-sm font-semibold hover:bg-primary/90 card-elev"
              data-testid="pc-dashboard"
            >
              Open dashboard <ArrowRight size={14} weight="bold" />
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-rose-50 text-rose-600 grid place-items-center mb-5">
              <XCircle size={30} weight="fill" />
            </div>
            <div className="text-overline text-rose-600 mb-2">Payment not confirmed</div>
            <h1 className="font-display text-2xl tracking-tight">We couldn't verify this transaction.</h1>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{state.message}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/subscribe" className="inline-flex items-center gap-2 border border-border rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-secondary">
                Try again
              </Link>
              <a href="mailto:support@orixa-invest.co" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90">
                Contact support
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
